import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Plus, RefreshCw, Search } from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

const STATUS_LABELS = {
  PENDING_PAYMENT: "En attente dépôt",
  A_VERIFIER: "À vérifier",
  HELD_IN_ESCROW: "Sécurisé",
  IN_PROGRESS: "En cours",
  DELIVERED: "Livré",
  UNDER_REVIEW: "En validation",
  DISPUTED: "Litige",
  RELEASED: "Payé",
  COMPLETED: "Clôturé",
};

const STATUS_COLORS = {
  PENDING_PAYMENT: "#B45309",
  A_VERIFIER: "#78350F",
  HELD_IN_ESCROW: "#009640",
  IN_PROGRESS: "#1E40AF",
  DELIVERED: "#1E40AF",
  UNDER_REVIEW: "#1E40AF",
  DISPUTED: "#991B1B",
  RELEASED: "#065F46",
  COMPLETED: "#065F46",
};

/**
 * MyEscrows (V3.17) — Liste des séquestres du user courant (talent + entreprise).
 * URL : /escrow
 */
export default function MyEscrows() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v1/escrow/mine${statusFilter ? `?status=${statusFilter}` : ""}`);
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) =>
      (it.id || "").toLowerCase().includes(needle) ||
      (it.title || "").toLowerCase().includes(needle)
    );
  }, [items, q]);

  const canCreate = ["employeur", "entrepreneur", "admin", "super-admin"].includes(user?.role);

  return (
    <PrivateLayout
      title="🛡️ Mes séquestres AFRICTAL"
      breadcrumbs={[{ label: "Séquestres" }]}
      actions={
        <div style={{ display: "flex", gap: 6 }}>
          <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={load} data-testid="my-escrows-refresh">
            <RefreshCw size={12} /> Actualiser
          </button>
          {canCreate && (
            <button className="afr-btn afr-btn--primary afr-btn--sm" onClick={() => setShowCreate((s) => !s)}
                    data-testid="my-escrows-create-btn">
              <Plus size={12} /> Nouveau séquestre
            </button>
          )}
        </div>
      }
    >
      <div data-testid="my-escrows-page">
        {showCreate && canCreate && (
          <CreateEscrowForm
            onCreated={(esc) => { setShowCreate(false); load(); toast.success("Séquestre créé"); }}
            onCancel={() => setShowCreate(false)}
          />
        )}

        <section className="afr-card" style={{ padding: 12, marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }} data-testid="my-escrows-status-filter">
              {["", ...Object.keys(STATUS_LABELS)].map((s) => (
                <button
                  key={s || "all"}
                  className={`afr-btn afr-btn--sm ${statusFilter === s ? "afr-btn--primary" : "afr-btn--outline"}`}
                  onClick={() => setStatusFilter(s)}
                  data-testid={`my-escrows-filter-${s || "all"}`}
                >
                  {s ? STATUS_LABELS[s] : "Tous"}
                </button>
              ))}
            </div>
            <div style={{ position: "relative", flex: "1 1 200px" }}>
              <Search size={14} style={{ position: "absolute", top: 9, left: 8, color: "#94a3b8" }} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher…"
                data-testid="my-escrows-search"
                style={{ width: "100%", padding: "6px 8px 6px 28px", border: "1px solid #E2E8F0", borderRadius: 6 }}
              />
            </div>
          </div>
        </section>

        <section className="afr-card" style={{ padding: 12 }}>
          {loading ? (
            <div>Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="afr-empty" data-testid="my-escrows-empty">
              <div className="afr-empty__title">
                <Shield size={20} style={{ verticalAlign: "middle" }} /> Aucun séquestre pour le moment
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {filtered.map((it) => (
                <Link
                  key={it.id}
                  to={`/escrow/${it.id}`}
                  data-testid={`my-escrow-row-${it.id}`}
                  className="afr-card"
                  style={{
                    padding: 12, display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 12,
                    alignItems: "center", textDecoration: "none", color: "inherit",
                    borderLeft: `4px solid ${STATUS_COLORS[it.status] || "#94a3b8"}`,
                  }}
                >
                  <Shield size={24} color={STATUS_COLORS[it.status] || "#94a3b8"} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{it.title || `Séquestre #${it.id.slice(0, 8)}`}</div>
                    <div style={{ fontSize: "0.78rem", color: "#64748b" }}>
                      {it.amount.toLocaleString()} {it.currency} · Plan {it.deposit_plan === "full" ? "100%" : "50/50"}
                    </div>
                  </div>
                  <span
                    style={{
                      padding: "4px 10px", borderRadius: 999, fontWeight: 700, fontSize: "0.72rem",
                      background: `${STATUS_COLORS[it.status]}15`, color: STATUS_COLORS[it.status],
                    }}
                  >
                    {STATUS_LABELS[it.status] || it.status}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </PrivateLayout>
  );
}

function CreateEscrowForm({ onCreated, onCancel }) {
  const [talentQ, setTalentQ] = useState("");
  const [talents, setTalents] = useState([]);
  const [talentId, setTalentId] = useState("");
  const [talentName, setTalentName] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("XOF");
  const [plan, setPlan] = useState("full");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!talentQ.trim()) return setTalents([]);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/v1/users/search?q=${encodeURIComponent(talentQ)}&limit=10`);
        setTalents(data.items || data.users || data.results || []);
      } catch { /* silent */ }
    }, 300);
    return () => clearTimeout(t);
  }, [talentQ]);

  const submit = async () => {
    if (!talentId) return toast.error("Sélectionnez un talent");
    if (!amount || Number(amount) <= 0) return toast.error("Montant invalide");
    setBusy(true);
    try {
      const { data } = await api.post("/v1/escrow", {
        talent_id: talentId,
        amount: Number(amount),
        currency,
        deposit_plan: plan,
        title: title || undefined,
        description: description || undefined,
      });
      onCreated(data.escrow);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="afr-card" style={{ padding: 16, marginBottom: 12, background: "#F8FAFC" }}
             data-testid="create-escrow-form">
      <h3 style={{ margin: "0 0 12px" }}>Nouveau séquestre</h3>
      <div style={{ display: "grid", gap: 10 }}>
        <div>
          <label style={label}>Talent bénéficiaire</label>
          <input value={talentName || talentQ} onChange={(e) => { setTalentQ(e.target.value); setTalentName(""); setTalentId(""); }}
                 placeholder="Rechercher un talent par nom ou email…"
                 data-testid="create-escrow-talent-search"
                 style={input} />
          {talents.length > 0 && !talentName && (
            <div style={{ marginTop: 4, maxHeight: 180, overflowY: "auto", border: "1px solid #E2E8F0", borderRadius: 6 }}>
              {talents.map((t) => (
                <div key={t.id}
                     onClick={() => { setTalentId(t.id); setTalentName(t.name || t.email); setTalents([]); }}
                     data-testid={`create-escrow-talent-${t.id}`}
                     style={{ padding: 8, cursor: "pointer", borderBottom: "1px solid #F1F5F9" }}>
                  <div style={{ fontWeight: 600 }}>{t.name || t.email}</div>
                  <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>{t.role}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
          <div>
            <label style={label}>Montant</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="create-escrow-amount" style={input} />
          </div>
          <div>
            <label style={label}>Devise</label>
            <select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="create-escrow-currency" style={input}>
              <option>XOF</option><option>XAF</option><option>EUR</option><option>USD</option>
            </select>
          </div>
        </div>
        <div>
          <label style={label}>Plan de dépôt</label>
          <div style={{ display: "flex", gap: 8 }}>
            {[
              { v: "full", label: "100 % avant démarrage" },
              { v: "split_50_50", label: "50 % + 50 %" },
            ].map((p) => (
              <button key={p.v} type="button"
                      className={`afr-btn afr-btn--sm ${plan === p.v ? "afr-btn--primary" : "afr-btn--outline"}`}
                      onClick={() => setPlan(p.v)}
                      data-testid={`create-escrow-plan-${p.v}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label style={label}>Titre (optionnel)</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="create-escrow-title" style={input} />
        </div>
        <div>
          <label style={label}>Description (optionnel)</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                    rows={2} data-testid="create-escrow-description"
                    style={{ ...input, resize: "vertical" }} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
        <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={onCancel}>Annuler</button>
        <button className="afr-btn afr-btn--primary afr-btn--sm" onClick={submit} disabled={busy}
                data-testid="create-escrow-submit">
          {busy ? "Création…" : "Créer et procéder au dépôt"}
        </button>
      </div>
    </section>
  );
}

const label = { display: "block", fontSize: "0.8rem", color: "#475569", marginBottom: 4, fontWeight: 600 };
const input = { width: "100%", padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: "0.9rem" };
